import { Record, List, Map } from 'immutable';
import DesignSection from 'records/DesignSection';
import StringUtilities from 'records/StringUtilities';
import CurrencyUtilities from 'records/CurrencyUtilities';
import DesignItem from 'records/DesignItem';
import _ from 'lodash';
import DesignPage from 'records/DesignPage';

const CONCURRENCY_PATTERN = /^\$?-?[0-9]+(\.[0-9]+)?$/;

export const TYPES = {
  BRAND: 'brand',
  MENU: 'menu',
  DESIGN: 'design',
  MENU_ITEM: 'menu-item',
  CATEGORY: 'category',
  LOCATION: 'location',
  BUZZCARD: 'buzzcard',
  POSTER: 'poster',
};

const DEFAULT_CATEGORY = 19; // General

const defaultValues = {
  designKey: null,
  valueKey: null,
  id: null,
  type: null,
  value: null,
  insertToCanvas: true,
  page: -1,
};

export default class DesignItemMapping extends Record(defaultValues, DesignItemMapping) {
  constructor(values) {
    super({
      ...values,
    });
  }

  static addDesignItemMapping(designItemMappings, designItemMapping) {
    return designItemMappings.push(designItemMapping);
  }

  static addBrandDesignItems(designItemMappings, brand) {
    return DesignItemMapping.addDesignItemMapping(designItemMappings,
      new DesignItemMapping({
        designKey: 'brand-name',
        valueKey: 'brandName',
        id: brand.get('id'),
        type: TYPES.BRAND,
        value: brand.get('brandName'),
      })
    );
  }

  static addBuzzCardDesignItems(designItemMappings, buzzCards, designPages) {
    let result = designItemMappings;
    buzzCards.map((buzzCard) => {
      const buzzCardPage = DesignPage.getDesignPageOrdinalByPageId(designPages, buzzCard.get('designPageId'));
      result = DesignItemMapping.addDesignItemMapping(result,
        new DesignItemMapping({
          designKey: 'brand-tagline',
          valueKey: 'frontCallout',
          id: buzzCard.get('id'),
          type: TYPES.BUZZCARD,
          value: buzzCard.get('frontCallout'),
          page: buzzCardPage.get('ordinal'),
        })
      );
    });
    return result;
  }

  static addPosterDesignItems(designItemMappings, poster) {
    let result = DesignItemMapping.addDesignItemMapping(designItemMappings,
      new DesignItemMapping({
        designKey: 'menu-description',
        valueKey: 'message',
        id: poster.get('id'),
        type: TYPES.POSTER,
        value: poster.get('message'),
      })
    );
    result = DesignItemMapping.addDesignItemMapping(result,
      new DesignItemMapping({
        designKey: 'brand-tagline',
        valueKey: 'title',
        id: poster.get('id'),
        type: TYPES.POSTER,
        value: poster.get('title'),
      })
    );

    return result;
  }

  static addDesignMenuDesc(designItemMappings, design) {
    let result = DesignItemMapping.addDesignItemMapping(designItemMappings,
      new DesignItemMapping({
        designKey: 'menu-description',
        valueKey: 'menuDescription',
        id: design.get('id'),
        type: TYPES.DESIGN,
        value: design.get('menuDescription'),
      })
    );
    return result;
  }

  static addMenuDesignItems(designItemMappings, menu) {
    let result = DesignItemMapping.addDesignItemMapping(designItemMappings,
      new DesignItemMapping({
        designKey: 'menu-title',
        valueKey: 'title',
        id: menu.get('id'),
        type: TYPES.MENU,
        value: menu.get('title'),
      })
    );
    /*result = DesignItemMapping.addDesignItemMapping(result,
      new DesignItemMapping({
        designKey: 'menu-description',
        valueKey: 'description',
        id: menu.get('id'),
        type: TYPES.MENU,
        value: menu.get('description'),
      })
    );*/
    return result;
  }

  static addAddressDesignItems(designItemMappings, locationId, address) {
    let result = DesignItemMapping.addDesignItemMapping(designItemMappings,
      new DesignItemMapping({
        designKey: 'location-street-address-1',
        valueKey: 'street1',
        id: locationId,
        type: TYPES.LOCATION,
        value: address.get('street1') || '',
      })
    );
    result = DesignItemMapping.addDesignItemMapping(result,
      new DesignItemMapping({
        designKey: 'location-street-address-2',
        valueKey: 'street2',
        id: locationId,
        type: TYPES.LOCATION,
        value: address.get('street2'),
      })
    );
    result = DesignItemMapping.addDesignItemMapping(result,
      new DesignItemMapping({
        designKey: 'location-city',
        valueKey: 'city',
        id: locationId,
        type: TYPES.LOCATION,
        value: address.get('city'),
      })
    );
    result = DesignItemMapping.addDesignItemMapping(result,
      new DesignItemMapping({
        designKey: 'location-state',
        valueKey: 'state',
        id: locationId,
        type: TYPES.LOCATION,
        value: address.get('state'),
      })
    );
    result = DesignItemMapping.addDesignItemMapping(result,
      new DesignItemMapping({
        designKey: 'location-postalcode',
        valueKey: 'postalCode',
        id: locationId,
        type: TYPES.LOCATION,
        value: address.get('postalCode'),
      })
    );

    return result;
  }

  static addDesignPages(designItemMappings, designPages,designSectionId,designItemId) {
    let result = designItemMappings;
    designPages.forEach((designPage) => {
      let flattenedSections = new List();
      flattenedSections = flattenedSections.concat(designPage.get('designSections'));
      flattenedSections = DesignSection.sortDesignSectionsByOrdinal(flattenedSections);
      result = DesignItemMapping.addDesignSections(result, flattenedSections, designPage.get('ordinal'));
      result = DesignItemMapping.addDesignItemsFromDesignSections(result, flattenedSections, designPage.get('ordinal'),designSectionId,designItemId);
    });
    return result;
  }

  static addDesignSections(designItemMappings, designSections, pageIndex) {
    let result = designItemMappings;
    designSections.forEach((designSection, index) => {
      let nameKey;
      let descKey;
      if (index >= 10) {
        nameKey = `menu-section-00${ index }-name`;
        descKey = `menu-section-00${ index }-description`;
      } else {
        nameKey = `menu-section-000${ index }-name`;
        descKey = `menu-section-000${ index }-description`;
      }
      result = DesignItemMapping.addDesignItemMapping(result, new DesignItemMapping({
        designKey: nameKey,
        valueKey: 'name',
        id: designSection.get('id'),
        type: TYPES.CATEGORY,
        value: designSection.get('name'),
        page: pageIndex,
        insertToCanvas: designSection.get('loadToCanvas'),
      }));

      result = DesignItemMapping.addDesignItemMapping(result, new DesignItemMapping({
        designKey: descKey,
        valueKey: 'description',
        id: designSection.get('id'),
        type: TYPES.CATEGORY,
        value: designSection.get('description'),
        page: pageIndex,
        insertToCanvas: designSection.get('loadToCanvas'),
      }));
    });
    return result;
  }

  static addDesignItemsFromDesignSections(designItemMappings, designSections, pageIndex,designSectionId,designItemId) {
    let result = designItemMappings;
    let itemIndex = 0;
    designSections.forEach((designSection) => {
      designSection.get('designItems').forEach((designItem) => {
        let nameKey;
        let descKey;
        let imageKey;
        if (itemIndex >= 10) {
          nameKey = `menu-item-00${ itemIndex }-name`;
          descKey = `menu-item-00${ itemIndex }-description`;
          imageKey = `menu-item-00${ itemIndex }-image`;
        } else {
          nameKey = `menu-item-000${ itemIndex }-name`;
          descKey = `menu-item-000${ itemIndex }-description`;
          imageKey = `menu-item-000${ itemIndex }-image`;
        }
        result = DesignItemMapping.addDesignItemMapping(result, new DesignItemMapping({
          designKey: nameKey,
          valueKey: 'title',
          id: designItem.get('id'),
          insertToCanvas: designItem.get('loadToCanvas'),
          type: TYPES.MENU_ITEM,
          value: designItem.get('title'),
          page: pageIndex,
        }));

        // MM-1230 combine price with desc
        const designItemDesc = designItem.get('description')
        ? designItem.get('description')
        : 'Add a description';

        result = DesignItemMapping.addDesignItemMapping(result, new DesignItemMapping({
          designKey: descKey,
          valueKey: 'description',
          id: designItem.get('id'),
          insertToCanvas: designItem.get('loadToCanvas'),
          type: TYPES.MENU_ITEM,
          value: designItemDesc,
          page: pageIndex,
        }));
        result = DesignItemMapping.addDesignItemMapping(result, new DesignItemMapping({
          designKey: imageKey,
          valueKey: 'menuItemImageName',
          id: designItem.get('id'),
          insertToCanvas: designItem.get('loadToCanvas'),
          type: TYPES.MENU_ITEM,
          value: designItem.get('menuItemImageName') ? `user:${ designItem.get('menuItemImageName') }` : '',
          page: pageIndex,
        }));
        itemIndex++;
      });
    });
    return result;
  }

  /*static addMenuItemDesignItems(designItemMappings, menuItems) {
    let result = designItemMappings;
    menuItems.forEach((menuItem, index) => {
      let nameKey;
      let descKey;
      let priceKey;
      let imageKey;

      if (index >= 10) {
        nameKey = `menu-item-00${ index }-name`;
        descKey = `menu-item-00${ index }-description`;
        priceKey = `menu-item-00${ index }-price`;
        imageKey = `menu-item-00${ index }-image`;
      } else {
        nameKey = `menu-item-000${ index }-name`;
        descKey = `menu-item-000${ index }-description`;
        priceKey = `menu-item-000${ index }-price`;
        imageKey = `menu-item-000${ index }-image`;
      }
      result = DesignItemMapping.addDesignItemMapping(result, new DesignItemMapping({
        designKey: nameKey,
        valueKey: 'title',
        id: menuItem.id,
        type: TYPES.MENU_ITEM,
        value: menuItem.title || '',
      }));
      result = DesignItemMapping.addDesignItemMapping(result, new DesignItemMapping({
        designKey: descKey,
        valueKey: 'description',
        id: menuItem.id,
        type: TYPES.MENU_ITEM,
        value: menuItem.description || '',
      }));
      result = DesignItemMapping.addDesignItemMapping(result, new DesignItemMapping({
        designKey: priceKey,
        valueKey: 'price',
        id: menuItem.id,
        type: TYPES.MENU_ITEM,
        value: menuItem.price ? `${ menuItem.price }` : '0',
      }));
      result = DesignItemMapping.addDesignItemMapping(result, new DesignItemMapping({
        designKey: imageKey,
        valueKey: 'imageName',
        id: menuItem.id,
        type: TYPES.MENU_ITEM,
        value: `user:${ menuItem.imageName }` || '',
      }));
    });
    return result;
  } */

  static getCustomerCanvasUserDataFromDesignItemMapping(designItemMappings) {
    const userData = { };
    designItemMappings.forEach((item) => {
      if (item.get('insertToCanvas')) {
        if (item.get('page') > -1) {
          if (userData.surfaces === undefined) {
            userData.surfaces = {};
          }
          if (userData.surfaces[`${ item.get('page') }`] === undefined) {
            userData.surfaces[`${ item.get('page') }`] = {};
          }
          userData.surfaces[`${ item.get('page') }`][item.get('designKey')] = item.get('value');
        } else {
          userData[item.get('designKey')] = item.get('value');
        }
      }
    });
    return userData;
  }

  static filterByType(designItemMappings, type) {
    return designItemMappings.filter((item) => {
      return item.get('type') === type;
    });
  }

  static findUniqueIds(designItemMappings) {
    const ids = [];
    designItemMappings.forEach((item) => {
      if (!ids.includes(item.get('id'))) {
        ids.push(item.get('id'));
      }
    });
    return ids;
  }

  static filterById(designItemMappings, id) {
    return designItemMappings.filter((item) => {
      return item.get('id') === id;
    });
  }

  static updateDesignMapsValuesFromUserChanges(designItemMappings, userChanges, surfaceDef, currentPageIndex) {
    const { surfaces } = userChanges;
    let updatedMappings = new List();
    // process each page's change
    _.forEach(surfaces, (surface, name) => {
      const { texts: surfaceTexts } = surface;
      const pageIndex = surfaceDef[name];
      surfaceTexts.forEach((userChange) => {
        let menuItem = DesignItemMapping.findByDesignKey(designItemMappings, userChange.name);
        if (menuItem && menuItem.get('page') === -1 &&
        (currentPageIndex === pageIndex || userChange.name === 'brand-name')) {
          menuItem = menuItem.set('value', userChange.usersValue);
          updatedMappings = updatedMappings.push(menuItem);
        }

        let item = DesignItemMapping
          .findByDesignKeyAndPage(designItemMappings, userChange.name, pageIndex);
        if (item) {
          item = item.set('value', userChange.usersValue);
          updatedMappings = updatedMappings.push(item);
        }
      });
    });
    return updatedMappings;
  }

  static updateByDesignKey(designItemMappings, designKey, value) {
    const index = DesignItemMapping.findIndexByDesignKey(designItemMappings, designKey);
    return designItemMappings.update(index, item => item.set('value', value));
  }

  static findIndexByDesignKey(designItemMappings, designKey) {
    return designItemMappings.findIndex((item) => {
      return item.get('designKey') === designKey;
    });
  }

  static findByDesignKey(designItemMappings, designKey) {
    return designItemMappings.find((item) => {
      return item.get('designKey') === designKey;
    });
  }

  static findByDesignKeyAndPage(designItemMappings, designKey, pageIndex) {
    return designItemMappings.find((item) => {
      return item.get('designKey') === designKey && item.get('page') === pageIndex;
    });
  }

  static getDesignItemMappingByTypeAndId(designItemMappings, type, id) {
    return designItemMappings.find((item) => {
      return item.get('type') === type && item.get('id') === id ? item : undefined;
    });
  }

  static getDesignItemMappingByType(designItemMappings, type) {
    return designItemMappings.filter((item) => {
      return item.get('type') === type;
    });
  }

  static abnormalChangedIds(designItemMappings, defaultValue) {
    let ids = new List();
    designItemMappings.forEach((item) => {
      if (item.get('value') == defaultValue &&  item.get('type') == TYPES.MENU_ITEM) {
        ids = ids.push(item.get('id'));
      }
    });
    return ids;
  }

  static getDesignItemMappingByDesignItemIds(designItemMappings, abnormalIds) {
    return designItemMappings.filter((item) => {
      return abnormalIds.indexOf(item.get('id')) == -1;
    });
  }

  static createDesignItemListsFromDesignMappings(designItemMappings, design) {
    let create = new List();
    let filteredMenuItems = this.getDesignItemMappingByType(designItemMappings, TYPES.MENU_ITEM);
    const abnormalNameIds = this.abnormalChangedIds(filteredMenuItems, 'Name');
    const abnormalDescIds = this.abnormalChangedIds(filteredMenuItems, 'Description ');
    const abnormalIds = abnormalNameIds.toSet().intersect(abnormalDescIds.toSet()).toList();
    filteredMenuItems = this.getDesignItemMappingByDesignItemIds(filteredMenuItems, abnormalIds);
    const uniqueMenuItemIds = this.findUniqueIds(filteredMenuItems);

    uniqueMenuItemIds.forEach((uniqueId) => {
      // Find design item in designItem and coresponding menuItem if existing
      let designItem = new DesignItem();
      //const menuItem = MenuItem.getMenuItemById(menuItems, uniqueId);
      // Filter Items list to contain one menu item mappings
      const menuItemMappings = this.filterById(filteredMenuItems, uniqueId);

      const linkedDesignItem = design.findDesignItemById(uniqueId);
      designItem = new DesignItem({
        id: linkedDesignItem.get('id'),
        userCategoryId: linkedDesignItem.get('userCategoryId'),
        menuItemId: linkedDesignItem.get('menuItemId'),
        ordinal: linkedDesignItem.get('ordinal'),
        designSectionId: linkedDesignItem.get('designSectionId'),
        loadToCanvas: true,
        title: linkedDesignItem.get('title'),
        description: linkedDesignItem.get('description'),
      });

      // Update the design item values
      menuItemMappings.forEach((itemMapping) => {
        const valueKey = itemMapping.get('valueKey');
        const newValue = itemMapping.get('value');
        if (valueKey === 'title') {
          designItem = designItem.set(
            `${ valueKey }`, StringUtilities.revertSpecialHtmlCharacters(newValue)
          );
        }
        if (valueKey === 'description') {
          // parse price value
          const descText = StringUtilities.revertSpecialHtmlCharacters(newValue);
          const splitterIndex = descText.lastIndexOf(' ');
          let priceValue = 0;
          if (splitterIndex > -1) {
            const priceText = descText.substr(splitterIndex + 1).trim();
            if (CONCURRENCY_PATTERN.test(priceText)) {
              priceValue = CurrencyUtilities.formatAndValidateCurrencyFromCanvas(priceText);
            }
          }
          designItem = designItem.set(
            'description', newValue
          );
          designItem = designItem.set(
            'price', priceValue
          );
        }
      });

      // Add design item to the create or update list
      create = create.push(designItem);
    });
    return create;
  }

  static createDesignSectionsListFromDesignMappings(designItemMappings, design) {
    const designSectionMappings = this.getDesignItemMappingByType(
      designItemMappings, TYPES.CATEGORY
    );
    const designPages = design.get('designPages');
    let flattenedSections = new List();
    let updatedSections = Map();
    designPages.forEach((designPage) => {
      flattenedSections = flattenedSections.concat(designPage.get('designSections'));
    });
    designSectionMappings.forEach((item) => {
      let designSection = DesignSection.getDesignSectionById(flattenedSections, item.get('id'));
      if (updatedSections.has(designSection.get('id'))) {
        designSection = updatedSections.get(designSection.get('id'));
      }
      designSection = designSection.set(
        `${ item.get('valueKey') }`, StringUtilities.revertSpecialHtmlCharacters(item.get('value'))
      );
      updatedSections = updatedSections.set(designSection.get('id'), designSection);
    });
    return updatedSections.toList();
  }

  static createDesignMenuFromDesignMappings(designItemMappings, menu) {
    const menuItemMappings = this.getDesignItemMappingByType(
      designItemMappings, TYPES.MENU
    );
    let newMenu = menu;
    menuItemMappings.forEach((item) => {
      newMenu = newMenu.set(
        `${ item.get('valueKey') }`, StringUtilities.revertSpecialHtmlCharacters(item.get('value'))
      );
    });
    return newMenu;
  }

  static getDesignMenuDescription(designItemMappings, design, designParameter, userChanges, surfaceDef) {
      const pageIndex = designParameter.get('currentDesignPageIndex');
      let designMenuDesc = design.get('menuDescription');
      if (design.get('menuId') > 0) {
        // filter out the Buzz card & Poster designs
        const newDesignMappings = DesignItemMapping.updateDesignMapsValuesFromUserChanges(
          designItemMappings,
          userChanges,
          surfaceDef,
          pageIndex
        );
        designMenuDesc = DesignItemMapping.fetchDesignMenuDescFromDesignMappings(newDesignMappings, designMenuDesc);
      }
      return designMenuDesc;
  }

  static fetchDesignMenuDescFromDesignMappings(designItemMappings, designMenuDesc) {
    const designMappings = this.getDesignItemMappingByType(
      designItemMappings, TYPES.DESIGN
    );
    designMappings.forEach((item) => {
      designMenuDesc = StringUtilities.revertSpecialHtmlCharacters(item.get('value'));
    });
    return designMenuDesc;
  }

  static createBrandNameFromDesignMappings(designItemMappings) {
    const brandMapping = this.getDesignItemMappingByType(
      designItemMappings, TYPES.BRAND
    );
    if(brandMapping.size > 0) {
      return StringUtilities.revertSpecialHtmlCharacters(brandMapping.first().get('value'));
    }
    return null;
  }

  static createDesignBuzzCardFromDesignMappings(designItemMappings, buzzCards, designPages) {
    const buzzCardMappings = this.getDesignItemMappingByType(
      designItemMappings, TYPES.BUZZCARD
    );
    let newBuzzCard = new List();
    buzzCardMappings.forEach((item) => {
      buzzCards.map((buzzcard) => {
        if(item.get('id') === buzzcard.get('id')) {
          buzzcard = buzzcard.set(
            `${ item.get('valueKey') }`, StringUtilities.revertSpecialHtmlCharacters(item.get('value'))
          );
          newBuzzCard = newBuzzCard.push(buzzcard);
        }
      });
    });
    return newBuzzCard;
  }

  static createDesignPosterFromDesignMappings(designItemMappings, poster) {
    const posterMappings = this.getDesignItemMappingByType(
      designItemMappings, TYPES.POSTER
    );
    let newPoster = poster;
    posterMappings.forEach((item) => {
      newPoster = newPoster.set(
        `${ item.get('valueKey') }`, StringUtilities.revertSpecialHtmlCharacters(item.get('value'))
      );
    });
    return newPoster;
  }

  static filterByPageNo(designItemMappings, pageIndex) {
    return designItemMappings.filter((item) => {
      return item.get('page') === pageIndex;
    });
  }

  static filteroutByPages(designItemMappings, firstPageId, secondPageId) {
    return designItemMappings.filter((item) => {
      return item.get('page') !== firstPageId && item.get('page') !== secondPageId;
    });
  }

  static swapTwoDesignPages(designItemMappings, firstPageId, secondPageId) {
    let firstPageItemMappings;
    let secondPageItemMappings;
    let otherPageItemMappings;

    firstPageItemMappings = DesignItemMapping.filterByPageNo(designItemMappings, firstPageId);
    secondPageItemMappings = DesignItemMapping.filterByPageNo(designItemMappings, secondPageId);
    otherPageItemMappings = DesignItemMapping.filteroutByPages(designItemMappings, firstPageId, secondPageId);

    firstPageItemMappings = firstPageItemMappings.map((pageData) => {
      return pageData.set('page', secondPageId);
    });

    secondPageItemMappings = secondPageItemMappings.map((pageData) => {
      return pageData.set('page', firstPageId);
    });

    return otherPageItemMappings.concat(firstPageItemMappings).concat(secondPageItemMappings);
  }

  static filterByPageRange(designItemMappings, pageIndex, forwardFlag) {
    if(forwardFlag) {
      return designItemMappings.filter((item) => {
        return item.get('page') >= pageIndex;
      });
    } else {
      return designItemMappings.filter((item) => {
        return item.get('page') < pageIndex;
      });
    }
  }

  static addPageInDesignMappings(designItemMappings, newPageIndex) {
    let forwardPageItemMappings;
    let otherPageItemMappings;
    forwardPageItemMappings = DesignItemMapping.filterByPageRange(designItemMappings, newPageIndex, true);
    otherPageItemMappings = DesignItemMapping.filterByPageRange(designItemMappings, newPageIndex, false);
    forwardPageItemMappings = forwardPageItemMappings.map((pageData) => {
      return pageData.set('page', pageData.get('page') + 1);
    });

    return otherPageItemMappings.concat(forwardPageItemMappings);
  }

  static deletePageIndexInDesignMappings(designItemMappings, newPageIndex) {
    let forwardPageItemMappings;
    let otherPageItemMappings;
    forwardPageItemMappings = DesignItemMapping.filterByPageRange(designItemMappings, newPageIndex, true);
    forwardPageItemMappings = forwardPageItemMappings.filter((itemMapping) => {
      return itemMapping.get('page') != newPageIndex;
    });
    otherPageItemMappings = DesignItemMapping.filterByPageRange(designItemMappings, newPageIndex, false);
    forwardPageItemMappings = forwardPageItemMappings.map((pageData) => {
      return pageData.set('page', pageData.get('page') - 1);
    });

    return otherPageItemMappings.concat(forwardPageItemMappings);
  }
}

export const DesignItemMappings = (data) => {
  let designItemMappings = new List();
  if (data) {
    data.forEach((value) => {
      designItemMappings = designItemMappings.push(new DesignItemMapping(value));
    });
  }
  return designItemMappings;
};
